import React, { useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";

let openModalCount = 0;

const ModalBase = ({
	isOpen = false,
	onClose = () => { },
	children,
	className = "",
	closeOnOutsideClick = true,
	showCloseButton = true,
	size = "md", // sm, md, lg, xl
	zIndex = 1000,
}) => {
	const { t } = useTranslation();
	const modalRef = useRef(null);
	const closeOnOutsideClickRef = useRef(closeOnOutsideClick);
	const onCloseRef = useRef(onClose);
	const zIndexRef = useRef(zIndex);

	closeOnOutsideClickRef.current = closeOnOutsideClick;
	onCloseRef.current = onClose;
	zIndexRef.current = zIndex;

	const handleClickOutside = (event) => {
		if (!closeOnOutsideClickRef.current || !modalRef.current) {
			return;
		}

		if (modalRef.current.contains(event.target)) {
			return;
		}

		const clickedOverlay = event.target.closest("[data-modal-overlay]");
		if (clickedOverlay) {
			const clickedZIndex = Number(clickedOverlay.getAttribute("data-modal-z-index") || 0);
			if (clickedZIndex > zIndexRef.current) {
				return;
			}
		}

		onCloseRef.current();
	};

	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}

		openModalCount += 1;
		document.body.style.overflow = "hidden";
		document.addEventListener("mousedown", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			openModalCount = Math.max(0, openModalCount - 1);
			if (openModalCount === 0) {
				document.body.style.overflow = "";
			}
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps -- listener uses refs for latest props
	}, [isOpen]);

	if (!isOpen) return null;

	const sizeClass = {
		sm: "max-w-sm",
		md: "max-w-md",
		mdh: "max-w-md",
		lg: "max-w-5xl",
		xl: "max-w-[95vw] h-[90vh]",
	}[size];

	const modalContent = (
		<div
			className="fixed inset-0 flex items-start justify-center bg-black/40 p-2 backdrop-blur-sm sm:items-center sm:p-4"
			style={{ zIndex }}
			data-modal-overlay
			data-modal-z-index={zIndex}
			aria-modal="true"
			role="dialog"
		>
			<div
				ref={modalRef}
				className={`relative max-h-[calc(100dvh-1rem)] overflow-hidden rounded-2xl bg-mute-25 px-4 py-4 shadow-2xl sm:max-h-[calc(100dvh-2rem)] sm:w-[95%] sm:px-8 sm:py-8 sm:pt-14 sm:pb-8 ${sizeClass} animate-fadeIn ${className}`}
			>
				{showCloseButton && (
					<button
						type="button"
						onClick={() => onCloseRef.current()}
						className="absolute top-5 right-5 text-mute-500 hover:text-mute-700 hover:bg-mute-100 rounded-full p-2 transition"
						aria-label={t("common.closeModal")}
					>
						<FontAwesomeIcon icon={faXmark} className="text-mute-500 text-xl" />
					</button>
				)}

				{children}
			</div>
		</div>
	);

	return createPortal(modalContent, document.body);
};

export default ModalBase;
